import { faThumbsUp, faTimes } from '@fortawesome/pro-solid-svg-icons';
import { ActionButton, ReturnStickerSelection } from 'components';
import { HeadingItem } from 'components/heading-item/heading-item';
import { TripContext } from 'contexts';
import { NumberBox, Popup, SelectBox } from 'devextreme-react';
import { MispickPopupField } from 'enums';
import React, { FC, useContext } from 'react';
import { useReturnItemService } from 'services';
import { MispicksReasons, ReconcileReasonType } from 'services/enums';
import { LineItem, Mispick, Order, OrderItem } from 'services/types';
import './mispicks-popup.scss';

type Props = {
    isVisible: boolean,
    isReturnStickerAvailable: boolean,
    mispicksItem: Mispick,
    stopId: number | undefined,
    onClickOk: () => void,
    onClickCancel: () => void,
    onValueChanged: (mispicksField: MispickPopupField, value: any) => void,
}

enum MispickReason {
    OnWrongLoad = "On Wrong Load",
    WrongItemSent = "Wrong Item Sent"
}

export const MispicksPopup: FC<Props> = ({ isVisible, isReturnStickerAvailable, mispicksItem, stopId, onClickOk, onValueChanged, onClickCancel }) => {

    const { isValidReturnSticker, isReturnStickerDuplicate } = useReturnItemService();

    const tripContext = useContext(TripContext)
    const stopOrders: Order[] = tripContext.orders.filter(o => o.stopId === stopId)
    const stopItems: OrderItem[] = tripContext.orderItems.filter(oi => stopOrders.some(o => o.id === oi.orderId)) ?? []
    const orderItems: OrderItem[] = stopItems.filter(items => tripContext.orderItemChanges.some(oic => oic.reconcileReasonId == ReconcileReasonType.Shortage && oic.orderItemId === items.id))
    const selectedShortedItem: OrderItem | undefined = mispicksItem.shortedOrderItemId ? orderItems.find(item => item.id == mispicksItem.shortedOrderItemId) : {} as OrderItem
    const lineItems: LineItem[] = tripContext.lineItems.filter(lineItem => orderItems.some(orderItem => orderItem.lineItemId == lineItem.id))
    const selectedLineItem: LineItem = lineItems.find(lineItem => selectedShortedItem?.lineItemId == lineItem.id) ?? {} as LineItem

    const hasValidLineItem: boolean = mispicksItem.lineItemId != null && mispicksItem.lineItemId.toString().trim().length < 7
    const hasValidQuantity: boolean = mispicksItem.quantity != null && mispicksItem.quantity > 0 && Number.isInteger(mispicksItem.quantity)
    const hasQuantityLessThanMax: boolean = mispicksItem.quantity != null && mispicksItem.quantity <= 999
    const hasValidReason: boolean = (mispicksItem.mispickReason == MispickReason.OnWrongLoad || (mispicksItem.mispickReason == MispickReason.WrongItemSent && mispicksItem.shortedOrderItemId != null))
    const hasValidReturnSticker: boolean = (isValidReturnSticker(mispicksItem.returnSticker) || mispicksItem.noReturnStickerReason != null)
    
    const isReturnStickerDuplicateResult: boolean = isReturnStickerDuplicate(mispicksItem.returnSticker, null, null, mispicksItem.id, null);

    const isValid: () => boolean = () => hasValidLineItem && hasValidQuantity && hasQuantityLessThanMax && hasValidReason && hasValidReturnSticker

    return (
        <Popup
            visible={isVisible}
            dragEnabled={false}
            closeOnOutsideClick={false}
            showTitle={false}
            width={330}
            height={530}
        >
            <div className="secondary-popup-card">
                <HeadingItem text={'Item Details'}></HeadingItem>
                <div className={'mispick-popup-desc'}>{mispicksItem.longDescription}</div>
                <div className={'mispick-popup-row'}>
                    <div className={'mispick-popup-label'}>Item ID</div>
                    <div className={'mispick-popup-field'} style={{ backgroundColor: (mispicksItem.upc != null) ? '#ebebeb' : 'transparent' }}>
                        <NumberBox
                            disabled={mispicksItem.upc != null}
                            value={mispicksItem.lineItemId ? mispicksItem.lineItemId : undefined}
                            onKeyUp={(e: any) => onValueChanged(MispickPopupField.LineItemId, e.event.target.value)}
                        />
                    </div>

                </div>
                {mispicksItem.lineItemId != null && !hasValidLineItem &&
                    <div className={'validation-error'}>This is not a valid Dot item ID. Did you accidentally enter the UPC or manufacturer ID?</div>
                }
                <div className={'mispick-popup-row'}>
                    <div className={'mispick-popup-label'}>Quantity</div>
                    <div className={'mispick-popup-field'}>
                        <NumberBox
                            value={mispicksItem.quantity ? mispicksItem.quantity : undefined}
                            onKeyUp={(e: any) => onValueChanged(MispickPopupField.Quantity, e.event.target.value)}
                        />
                    </div>
                </div>
                {mispicksItem.quantity != null && !hasValidQuantity &&
                    <div className={'validation-error'}>At least one case must be added as a mispick. No decimal allowed.</div>
                }
                {mispicksItem.quantity != null && !hasQuantityLessThanMax &&
                    <div className={'validation-error'}>This is not a valid quantity. Did you accidentally scan a UPC or return sticker?</div>
                }
                <div className={'mispick-popup-row'}>
                    <div className={'mispick-popup-label'}>Reason</div>
                    <div className={'mispick-popup-field'}>
                        <SelectBox
                            items={[MispickReason.OnWrongLoad, MispickReason.WrongItemSent]}
                            defaultValue={null}
                            value={mispicksItem.mispickReason}
                            itemRender={(itemData: string) => <div className={'reconcile-selectionbox'}>{itemData}</div>}
                            onValueChanged={(e: any) => e.value && onValueChanged(MispickPopupField.ReasonCode, e.value)}
                        >
                        </SelectBox>
                    </div>
                </div>
                {mispicksItem.mispickReason == MispicksReasons.WrongItemSent &&
                    <div className={'mispick-popup-row'}>
                        <div className={'mispick-popup-shored-item-label'}>Shorted Items</div>
                        <div className={'mispick-popup-field'}>
                            <SelectBox
                                items={lineItems}
                                displayExpr={selectedLineItem.longDescription ? 'longDescription' : 'description'}
                                defaultValue={null}
                                value={selectedLineItem}
                                itemRender={(itemData: LineItem) => <div className={'mispick-popup-selectionbox'}>{itemData.longDescription ?? itemData.description}</div>}
                                onValueChanged={(e: any) => e.value.id && onValueChanged(MispickPopupField.ShortedItemId, orderItems[orderItems.findIndex(oi => oi.lineItemId == e.value.id)].id)}
                            >
                            </SelectBox>
                        </div>
                    </div>
                }
                <div className={'mispick-popup-return-sticker-field'}>
                    <ReturnStickerSelection
                        isReturnStickerAvailable={isReturnStickerAvailable}
                        returnSticker={mispicksItem.returnSticker}
                        isReturnStickerDuplicate={isReturnStickerDuplicateResult}
                        noReturnStickerReason={mispicksItem.noReturnStickerReason}
                        onReturnStickerChanged={(value) => onValueChanged(MispickPopupField.ReturnSticker, value)}
                        onNoReturnStickerReasonChanged={(value) => onValueChanged(MispickPopupField.NoReturnSticker, value)}
                        handleRadioChecked={(value: any) => value == "Yes" ? onValueChanged(MispickPopupField.ReturnSticker, 0) :
                        onValueChanged(MispickPopupField.NoReturnSticker, "")}
                    />
                </div>
                <div className={'bottom-fix-action-row'}>
                    <ActionButton
                        icon={faTimes}
                        buttonColor={'#EB5757'}
                        onClick={onClickCancel}
                        buttonText={'Cancel'}
                        iconSize={'1x'}
                    />
                    <ActionButton
                        icon={faThumbsUp}
                        enabled={!isReturnStickerDuplicateResult && isValid()}
                        buttonColor={'#76AB21'}
                        onClick={onClickOk}
                        buttonText={'OK'}
                    />
                </div>
            </div>
        </Popup>
    )
}